import { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import RegisterStepUser from "../../pages/RegisterStepUser";
import { API_BASE } from "../../utils/api";

export default function ProfileUsersCreate() {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    // 🔒 user estable (NO re-render)
    const userRef = useRef(JSON.parse(localStorage.getItem("user")));
    const user = userRef.current;

    const hasLoadedRef = useRef(false);

    const [form, setForm] = useState({
        userCountry: "ECUADOR",
        discontinued: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // =========================
    // SEDES DEL CLIENTE (para poder elegir a que sede queda asignado el usuario nuevo)
    // Si el cliente solo tiene una sede, la lista vuelve vacia y el selector no se
    // muestra — se sigue mandando user.idShipTo como antes.
    // =========================
    const [shipTos, setShipTos] = useState([]);

    useEffect(() => {
        if (!user || user.groupId !== "     9") return;

        fetch(
            `${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${user.idEmployee}&myaccount_id=${user.myaccountId}&has_ship_to=true&id_customer=${user.idCustomer}`
        )
            .then((res) => res.json())
            .then((data) => setShipTos(Array.isArray(data) ? data.filter((s) => s.idShipTo) : []))
            .catch((err) => console.error("Error cargando sedes", err));
    }, []);

    // =========================
    // CARGAR USUARIO (SOLO 1 VEZ EN EDICIÓN)
    // =========================
    useEffect(() => {
        if (!user || user.groupId !== "     9") return;
        if (!isEdit) return;
        if (hasLoadedRef.current) return;

        hasLoadedRef.current = true;

        const fetchUser = async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/api-ultra/api/ws/ecommerce_users?id_employee=${id}`
                );

                const list = await res.json();
                const data = list && list.length ? list[0] : null;
                if (!data) return;

                setForm({
                    userCountry: data.country || "ECUADOR",
                    userFirstName: data.firstName,
                    userLastName: data.lastName,
                    username: data.userId || data.userId,
                    userEmail: data.eMaile || data.eMail,
                    userPhone: data.phone || data.homePhone,
                    userBirthDate: formatDateForInput(data.birthDate),
                    userAddress: data.address,
                    userCity: data.city,
                    companyId: data.company_id || data.companyId,
                    bodegaId: data.bodega_id || data.bodegaId,
                    idShipTo: data.idShipTo ?? data.id_ship_to ?? null,
                    discontinued: data.discontinued ?? false,
                });
            } catch (err) {
                console.error("Error cargando usuario", err);
            }
        };

        fetchUser();
    }, [id, isEdit]);

    const formatDateForInput = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return d.toISOString().split("T")[0];
    };

    // 🔒 PROTECCIÓN DEL MÓDULO
    if (!user || user.groupId !== "     9") {
        return null;
    }

    /* =========================
       VALIDATE STEP 2
    ========================== */
    const validateUserForm = () => {
        const e = {};
        if (!form.userFirstName) e.userFirstName = "Ingrese su nombre";
        if (!form.userLastName) e.userLastName = "Ingrese su apellido";
        if (!form.username) e.username = "Ingrese usuario";
        if (!form.userEmail) e.userEmail = "Ingrese e-mail";
        if (!form.userPhone) e.userPhone = "Ingrese celular";
 //       if (!form.userAddress) e.userAddress = "Ingrese la dirección";

        if (!isEdit) {
            if (!form.password) e.password = "Ingrese contraseña";
            if (!form.confirmPassword) e.confirmPassword = "Confirme contraseña";
            if (form.password !== form.confirmPassword)
                e.confirmPassword = "No coinciden";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // =========================
    // PAYLOAD (MISMA ESTRUCTURA)
    // =========================
    const buildUserPayload = () => {
        const payload = {
            username: form.username,
            eMail: form.userEmail,

            firstName: form.userFirstName,
            lastName: form.userLastName,
            homePhone: form.userPhone,
            birthDate: form.userBirthDate,

            address: form.userAddress,
            city: form.userCity,
            country: form.userCountry,

            companyId: form.companyId,
            bodegaId: form.bodegaId,
            idCustomer: user.idCustomer,
            title: "ASESOR",
            // Si el cliente tiene varias sedes, el selector manda: sin elegir
            // ninguna, el usuario queda como Matriz (idShipTo null). Si el
            // cliente tiene una sola sede (no hay selector), se mantiene el
            // comportamiento anterior de heredar la sede del creador.
            idShipTo: shipTos.length > 0 ? (form.idShipTo || null) : user.idShipTo,
            idCreated: user.idEmployee,
            discontinued: form.discontinued,
        };

        if (!isEdit || (isEdit && form.password)) {
            payload.password = form.password;
        }

        return payload;
    };

    const onFinish = async () => {
        if (!validateUserForm()) return;

        try {
            setLoading(true);

            const payload = buildUserPayload();

            const url = isEdit
                ? `${API_BASE}/customers-ultra/api/ws/update_ecommerce_user?id_employee=${id}`
                : `${API_BASE}/customers-ultra/api/ws/create_ecommerce_user`
                + `?myaccount_id=${user.myaccountId}`
                + `&company_id=${user.companyId}`
                + `&bodega_id=${user.bodegaId}`;

            const res = await fetch(url, {
                method: isEdit ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Error guardando usuario");

            history.push("/profile/users");
        } catch (err) {
            console.error("Error guardando usuario", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto mt-28 px-4">
            <RegisterStepUser
                form={form}
                errors={errors}
                setForm={setForm}
                setErrors={setErrors}
                shipTos={shipTos}
                onBack={() => history.goBack()}
                onFinish={onFinish}
                submitLabel={isEdit ? "Actualizar usuario" : "Guardar usuario"}
                loading={loading}
                isEdit={isEdit}
            />
        </div>
    );
}
